import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { sortByCreatedAt } from 'src/helper/work-order-utils';
import {
  addQCMaterial,
  deleteQCMaterial,
  getQCIssueExpandedData,
  getQualityCheckData,
  saveQualityCheckData,
  submitQualityCheckData,
  updateDriveOrLaborTime,
  updateLaborCost,
  updateQCMaterial,
} from 'src/redux/quality-check/api';
import type {
  QCInitialResponseType,
  QCIssueExpandedViewResponseType,
  QualityCheckTypeInitStateType,
} from 'src/redux/quality-check/types';

const extraReducers = (builder: ActionReducerMapBuilder<QualityCheckTypeInitStateType>) => {
  builder.addCase(getQualityCheckData().pending, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckFetching = true;
    state.agentCheck = null;
  });

  builder.addCase(
    getQualityCheckData().fulfilled,
    (state: QualityCheckTypeInitStateType, action: PayloadAction<QCInitialResponseType>) => {
      state.isQualityCheckFetching = false;
      const response = action?.payload;

      const workOrderIssueIds = [] as QualityCheckTypeInitStateType['workOrderIssueIds'];
      let workOrderIssueSource = {} as QualityCheckTypeInitStateType['workOrderIssueSource'];
      const tripIds = [] as QualityCheckTypeInitStateType['tripCostBreakdown']['tripIds'];
      let tripCostSource =
        {} as QualityCheckTypeInitStateType['tripCostBreakdown']['tripCostSource'];

      sortByCreatedAt(response?.work_order_issues || []).forEach((issue) => {
        workOrderIssueIds.push(issue?.work_order_issue_id);
        workOrderIssueSource = {
          ...workOrderIssueSource,
          [issue?.work_order_issue_id]: { ...issue },
        };
      });

      response?.cost_break_down?.trip_cost?.forEach((tripCost) => {
        tripIds.push(tripCost?.trip_id);
        tripCostSource = {
          ...tripCostSource,
          [tripCost?.trip_id]: { ...tripCost },
        };
      });

      state.workOrderId = response?.work_order_id;
      state.workOrderIssueIds = workOrderIssueIds;
      state.workOrderIssueSource = workOrderIssueSource;
      state.tripNumber = response?.trip_number;
      state.tripCostBreakdown = {
        ...(state.tripCostBreakdown || {}),
        tripCostSource,
        totalCost: response?.cost_break_down?.total_cost_in_cent,
        tripIds,
      };
      state.invoice_pdf_url = response?.invoice_pdf_url;
      state.agentCheck = response?.agent_check ?? null;
    },
  );

  builder.addCase(getQualityCheckData().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckFetching = false;
  });
  // submit quality check data
  builder.addCase(submitQualityCheckData().pending, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSubmitting = true;
  });
  builder.addCase(submitQualityCheckData().fulfilled, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSubmitting = false;
  });
  builder.addCase(submitQualityCheckData().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSubmitting = false;
  });
  // update drive or labor time
  builder.addCase(updateDriveOrLaborTime().pending, (state: QualityCheckTypeInitStateType) => {
    state.isTripCostBreakdownUpdating = true;
  });
  builder.addCase(
    updateDriveOrLaborTime().fulfilled,
    (state: QualityCheckTypeInitStateType, action) => {
      const { tripId, ...payload } = action.payload;
      const { type } = action.meta.arg;
      const duration = payload.duration_in_sec;
      const cost = payload.cost_in_cents;
      let costDifference = payload.cost_in_cents;
      if (type === 'drive') {
        costDifference -= state.tripCostBreakdown.tripCostSource[tripId].drive_time.cost_in_cents;
        state.tripCostBreakdown.tripCostSource[tripId].drive_time.duration_in_sec = duration;
        state.tripCostBreakdown.tripCostSource[tripId].drive_time.cost_in_cents = cost;
        state.tripCostBreakdown.tripCostSource[tripId].drive_time.drive_time_adjusted_by =
          payload?.drive_time_adjusted_by;
      }
      if (type === 'labor') {
        costDifference -= state.tripCostBreakdown.tripCostSource[tripId].labor_time.cost_in_cents;
        state.tripCostBreakdown.tripCostSource[tripId].labor_time.duration_in_sec = duration;
        state.tripCostBreakdown.tripCostSource[tripId].labor_time.cost_in_cents = cost;
        state.tripCostBreakdown.tripCostSource[tripId].labor_time.labor_time_adjusted_by =
          payload?.labor_time_adjusted_by;
      }
      state.tripCostBreakdown.tripCostSource[tripId].labor_cost_in_cents += costDifference;
      state.tripCostBreakdown.totalCost += costDifference;
      state.isTripCostBreakdownUpdating = false;
    },
  );
  builder.addCase(updateDriveOrLaborTime().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isTripCostBreakdownUpdating = false;
  });
  // update labor cost
  builder.addCase(updateLaborCost().pending, (state: QualityCheckTypeInitStateType) => {
    state.isTripCostBreakdownUpdating = true;
  });
  builder.addCase(updateLaborCost().fulfilled, (state: QualityCheckTypeInitStateType, action) => {
    const { tripId, labor_cost_in_cents: laborCostInCents } = action.payload;
    let costDifference = laborCostInCents;
    costDifference -= state.tripCostBreakdown.tripCostSource[tripId].labor_cost_in_cents;
    state.tripCostBreakdown.totalCost += costDifference;
    state.tripCostBreakdown.tripCostSource[tripId].labor_cost_in_cents = laborCostInCents;
    state.isTripCostBreakdownUpdating = false;
  });
  builder.addCase(updateLaborCost().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isTripCostBreakdownUpdating = false;
  });

  builder.addCase(getQCIssueExpandedData().pending, (state: QualityCheckTypeInitStateType) => {
    state.isQCIssueExpandedDataFetching = true;
  });

  builder.addCase(
    getQCIssueExpandedData().fulfilled,
    (
      state: QualityCheckTypeInitStateType,
      action: PayloadAction<QCIssueExpandedViewResponseType>,
    ) => {
      const response = action?.payload;
      state.isQCIssueExpandedDataFetching = false;
      const tripIds = [] as QualityCheckTypeInitStateType['expandedIssueSource'][string]['tripIds'];
      let tripSource =
        {} as QualityCheckTypeInitStateType['expandedIssueSource'][string]['tripSource'];
      const materialIds =
        [] as QualityCheckTypeInitStateType['expandedIssueSource'][string]['materialIds'];
      let materialSource =
        {} as QualityCheckTypeInitStateType['expandedIssueSource'][string]['materialSource'];

      (response?.trips || [])?.forEach((trip) => {
        tripIds?.push(trip?.trip_id);
        tripSource = {
          ...tripSource,
          [trip?.trip_id]: { ...trip },
        };
      });

      (response?.materials || [])?.forEach((material) => {
        materialIds?.push(material?.material_id);
        materialSource = {
          ...materialSource,
          [material?.material_id]: { ...material },
        };
      });

      state.expandedIssueSource = {
        [response?.work_order_issue_id]: {
          service_notes: response?.service_notes,
          workOrderIssueId: response?.work_order_issue_id,
          workOrderNumber: response?.work_order_number,
          tripIds,
          tripSource,
          materialIds,
          materialSource,
          quality_check_status: response?.quality_check_status,
          description: response?.description,
          title: response?.title,
          quote_task: response?.quote_task || null,
          issue_type: response?.issue_type,
          problem: response?.problem,
          appointment_type: response?.appointment_type,
          media: response?.media || [],
          unresolved_from: response?.unresolved_from,
        },
      };
      state.isQCIssueExpandedDataFetching = false;
    },
  );

  builder.addCase(getQCIssueExpandedData().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isQCIssueExpandedDataFetching = false;
  });

  // Add Material
  builder.addCase(addQCMaterial().pending, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = true;
  });

  builder.addCase(addQCMaterial().fulfilled, (state: QualityCheckTypeInitStateType, action) => {
    const { workOrderIssueId, ...materialData } = action.payload;

    const newMaterial = materialData;
    if (materialData.cost_type === 'per-unit') {
      newMaterial.cost_in_cents = materialData.unit_price_in_cents;
    }

    state.expandedIssueSource[workOrderIssueId].materialSource[materialData.material_id] =
      newMaterial;

    if (
      !state.expandedIssueSource[workOrderIssueId]?.materialIds?.includes(materialData.material_id)
    ) {
      state.expandedIssueSource[workOrderIssueId]?.materialIds?.push(materialData.material_id);
    }

    state.isAddingOrEditingMaterial = false;
  });

  builder.addCase(addQCMaterial().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = false;
  });

  // Update Material
  builder.addCase(updateQCMaterial().pending, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = true;
  });

  builder.addCase(updateQCMaterial().fulfilled, (state: QualityCheckTypeInitStateType, action) => {
    const { workOrderIssueId, ...materialData } = action.payload;

    const newMaterial = materialData;
    if (materialData.cost_type === 'per-unit') {
      newMaterial.cost_in_cents = materialData.unit_price_in_cents;
    }
    state.expandedIssueSource[workOrderIssueId].materialSource[materialData.material_id] =
      materialData;
    state.isAddingOrEditingMaterial = false;
  });

  builder.addCase(updateQCMaterial().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = false;
  });

  // Delete Material
  builder.addCase(deleteQCMaterial().pending, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = true;
  });

  builder.addCase(deleteQCMaterial().fulfilled, (state: QualityCheckTypeInitStateType, action) => {
    const { workOrderIssueId, materialId } = action.payload;
    state.expandedIssueSource[workOrderIssueId].materialIds = state.expandedIssueSource[
      workOrderIssueId
    ].materialIds.filter((id) => id !== materialId);

    state.isAddingOrEditingMaterial = false;
  });

  builder.addCase(deleteQCMaterial().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isAddingOrEditingMaterial = false;
  });

  builder.addCase(saveQualityCheckData().pending, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSaving = true;
  });

  builder.addCase(saveQualityCheckData().fulfilled, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSaving = false;
  });

  builder.addCase(saveQualityCheckData().rejected, (state: QualityCheckTypeInitStateType) => {
    state.isQualityCheckSaving = false;
  });
};

export default extraReducers;
